import React, { type ComponentProps } from "react";
import { Input } from "@sumprio/ui";
import { clampStringToMaxLength, formatCharacterCountHint } from "utils/limitedString";

type SumprioInputProps = ComponentProps<typeof Input>;

export type LimitedCharInputProps = Omit<
  SumprioInputProps,
  "value" | "onChange" | "maxLength" | "hintText" | "helperText"
> & {
  value: string;
  onValueChange: (_value: string) => void;
  maxLength: number;
  showCharacterCount?: boolean;
  hintText?: SumprioInputProps["hintText"];
  helperText?: SumprioInputProps["helperText"];
};

function mergeHintParts(...parts: Array<string | undefined>): string | undefined {
  const cleaned = parts.map((p) => (typeof p === "string" ? p.trim() : "")).filter(Boolean);
  if (cleaned.length === 0) return undefined;
  return cleaned.join(" · ");
}

export default function LimitedCharInput({
  value,
  onValueChange,
  maxLength,
  showCharacterCount = true,
  hintText,
  helperText,
  ...rest
}: LimitedCharInputProps) {
  const countHint =
    showCharacterCount && maxLength > 0
      ? formatCharacterCountHint(value.length, maxLength)
      : undefined;
  const mergedHint = mergeHintParts(hintText ?? helperText, countHint);

  return (
    <Input
      {...rest}
      value={value}
      maxLength={maxLength}
      hintText={mergedHint}
      onChange={(event) => {
        onValueChange(clampStringToMaxLength(event.currentTarget.value, maxLength));
      }}
    />
  );
}
